import { useContext, useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import ADND from '@aytm/lib/adnd/ADND'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

import { stopEvent } from '@aytm/lib/common/func'

import { STATIC_REPORTS_IDS, VIRTUAL_REPORTS_IDS } from '@aytm/app/stats/page/sidebar/reports/actions'

import { isSkipperGuidedReport } from '@aytm/app/stats/page/sidebar/reports/lib/skipperGuidedReport'
import { SKIPPER_REPORT_FEATURE } from '@aytm/app/stats/page/sidebar/reports/lib/skipperGuidedReportsConst'
import { decodeHtmlEntities } from '@aytm/app/stats/lib/render/decode-html-entities'

import { DataContext } from '@aytm/app/stats/page/sidebar/reports/ReportsPanel'

// components
import ReportPopup from '@aytm/app/stats/page/sidebar/reports/ReportPopup'

// styles
import style from '@aytm/app/stats/page/sidebar/reports/styles/Report.module.scss'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

// A drag source pairs only with a target of the same type, so the type is what parts the
// list into pools a row may be reordered within: guided Skipper rows, the grouped types,
// and everything else, each split again by whether the row is hidden.
const dndType = report => {
  const group = isSkipperGuidedReport(report)
    ? `-${report.type}-guided`
    : [...STATIC_REPORTS_IDS, ...VIRTUAL_REPORTS_IDS].includes(report.type)
    ? `-${report.type}`
    : ''

  return `report-${PageGlobals.WINDOW_UID}${group}${report.hidden ? '-hidden' : ''}`
}

const Report = ({
  model,
  reportId,
  position,
  dndItemsList,
  dndReportTypeRef,
  dndReportPositionRef,
  onReportIdChange
}) => {
  const { root, state, features, isSurveyGuest, isSurveyViewer, dispatch, disabled } = useContext(DataContext)

  const modelState = state[model.id] || {}

  const isNew = 'new' == modelState.status
  const isDeleted = 'deleted' == modelState.status
  const isActive = ['active', 'delete'].includes(modelState.status)
  const isCurrent = reportId == model.id
  const isChanged = true === model.changed
  const isDeleting = 'delete' == modelState.status
  // The row stays a spinner until the generation is ready even once it has adopted the
  // persisted report -- mid-flight that body is the stat server's placeholder, not a report.
  const isGenerating = 'generating' == model.current.status
  const isReady = 'ready' == model.current.status
  const isDisabled = disabled
  // A report the platform owns: it cannot be deleted whoever is looking, which is what the row says
  // with a padlock. 'concept' by name, not the whole static group -- a guided Skipper report is
  // editable, so the Skipper type is gated on provenance in the clause below rather than locked
  // outright.
  const isProtected =
    true === model.current.protected ||
    ['original', 'xpert', 'widget', 'concept', ...VIRTUAL_REPORTS_IDS].includes(model.current.type) ||
    ('skipper' == model.current.type && !isSkipperGuidedReport(model.current)) ||
    (features?.[SKIPPER_REPORT_FEATURE]?.disabled && isSkipperGuidedReport(model.current))
  const isHidden = true === model.current.hidden

  // the wider lock, which also covers a read-only session and a panel that is switched off -- those
  // withhold the delete without the report itself being undeletable, so they draw no padlock
  const isLocked = isProtected || isDisabled || isSurveyGuest || isSurveyViewer

  const [overflow, setOverflow] = useState(false)

  const titleRef = useRef(null)

  const dndListItemRef = useRef(null)
  const dndListItemHandleRef = useRef(null)

  const dndFetchData = () => {
    return { ...model }
  }

  const dndListItem =
    null !== dndItemsList
      ? ADND.targets.itemsList.listItem({
          type: dndType(model.current),
          list: dndItemsList
        })
      : null
  const dndListItemHandle =
    null !== dndItemsList
      ? ADND.sources
          .listItemHandle({
            type: dndType(model.current),
            item: dndListItem,
            cloneable: false,
            edgeScrolling: false
          })
          .fetchData(dndFetchData)
          .onStart(() => {
            dndReportTypeRef.current = model.current.type
            dndReportPositionRef.current = position
          })
          .onEnd(() => {
            dndReportTypeRef.current = null
            dndReportPositionRef.current = null
          })
      : null

  const onReportDelete = () => {
    dispatch({
      type: 'UPDATE_REPORT_STATE',
      request: {
        id: model.id,
        status: 'delete'
      }
    })
  }

  const onReportSelect = event => {
    stopEvent(event)
    onReportIdChange(model.id)
  }

  const onReportView = event => {
    stopEvent(event)
    // Address the finished report before reloading: the load reconciles the list, but without this
    // it comes back on whatever the URL already held -- for a user who never opened the report, the
    // Default Report rather than the one they clicked.
    onReportIdChange(model.id, () => window.location.reload())
  }

  const checkOverflow = () => {
    if (!titleRef.current) return

    const div = document.createElement('div')

    document.body.appendChild(div)

    div.style.position = 'absolute'
    div.style.left = '-1000px'
    div.style.top = '-1000px'
    div.style.width = '194px'

    div.style.fontSize = window.getComputedStyle(titleRef.current, null).getPropertyValue('font-size') || '14px'
    div.style.lineHeight = window.getComputedStyle(titleRef.current, null).getPropertyValue('line-height') || '16px'

    div.style.overflow = 'hidden'
    div.style['overflow-wrap'] = 'break-word'

    div.innerHTML = titleRef.current.innerHTML
    setOverflow(80 < div.clientHeight)

    div.parentNode.removeChild(div)
  }

  useEffect(() => {
    if (dndListItem) dndListItem.connect(dndListItemRef.current)

    return () => {
      if (dndListItem) dndListItem.disconnect()
    }
  }, [dndListItem])

  useEffect(() => {
    if (dndListItemHandle) dndListItemHandle.connect(dndListItemHandleRef.current)

    return () => {
      if (dndListItemHandle) dndListItemHandle.disconnect()
    }
  }, [dndListItemHandle])

  useEffect(() => {
    if ('new' == modelState.status) {
      dispatch({
        type: 'UPDATE_REPORT_STATE',
        request: {
          id: model.id,
          status: null
        }
      })

      dndListItemRef.current.scrollIntoView(false)
    }

    checkOverflow()
  }, [model.id, modelState.status, dispatch])

  useEffect(() => checkOverflow(), [model.current.title])

  const renderTitle = () => {
    const title = model.current.title || ''

    // Rendered as text, not markup: the title arrives escaped, so decoding it here shows the
    // user's literal characters while markup inside a title stays inert.
    return (
      <span ref={titleRef} className={classNames(style['title-text'], { [style['title-text-overflow']]: overflow })}>
        {decodeHtmlEntities(
          'xpert' == model.current.type
            ? 0 < title.length && 'xpert report' != title.trim().replace(/\s+/, ' ').toLowerCase()
              ? `Xpert Report: ${title}`
              : 'Xpert Report'
            : title
        )}
      </span>
    )
  }

  return (
    <div
      ref={dndListItemRef}
      data-testid={`stat-filters-report-${model.id}`}
      data-changed={isChanged}
      className={classNames(style.component, style[`component-${model.current.type}`], {
        [style['component-new']]: isNew,
        [style['component-deleted']]: isDeleted,
        [style['component-current']]: isCurrent,
        [style['component-active']]: isActive,
        [style['component-warning']]: isChanged,
        [style['component-error']]: isDeleting,
        [style['component-disabled']]: isDisabled
      })}
    >
      {isDisabled ? (
        // Deliberately not an anchor: a disabled sidebar offers no way in, and the spec pins it.
        <span className={style.title}>{renderTitle()}</span>
      ) : (
        <>
          {isGenerating || isReady || isLocked ? null : (
            <div className={style.delete} aria-label="Delete report" {...pressable(onReportDelete)}>
              <i className="icon-aytmfd-x-v2" />
            </div>
          )}
          {/* One anchor across generating / ready / settled: the title carries the ref the overflow
              measurement reads, and a parent per state re-attached it to a new node every time a
              report finished generating. */}
          <a
            href={`#charts/${model.id}`}
            className={classNames(style.title, {
              [style.generating]: isGenerating,
              [style.ready]: isReady
            })}
            onClick={onReportSelect}
          >
            {isGenerating ? <i className="pi pi-spin pi-spinner" /> : null}
            {renderTitle()}
            {isReady ? (
              <span className={style['ready-view']} onClick={onReportView}>
                View report
              </span>
            ) : null}
          </a>
        </>
      )}
      {isProtected ? (
        <span className={style.icon} title="This report cannot be deleted">
          <i className="icon-aytmfd-lock" />
        </span>
      ) : null}
      {isHidden ? (
        <span className={style.icon} title="This report is invisible to clients">
          <i className="icon-aytmfd-invisible" />
        </span>
      ) : null}
      <ReportPopup root={root} model={model} position={position} />
      {!(
        ['skipper', 'concept'].includes(model.current.type) &&
        (['draft', 'generating'].includes(model.current.status) || model.current.hidden)
      ) &&
      !VIRTUAL_REPORTS_IDS.includes(model.current.type) &&
      !isDisabled ? (
        <div ref={dndListItemHandleRef} className={style.handle}>
          <i className="icon-aytmfd-handle" />
        </div>
      ) : null}
    </div>
  )
}

Report.propTypes = {
  model: PropTypes.object.isRequired,
  reportId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  position: PropTypes.number,
  dndItemsList: PropTypes.any,
  dndReportTypeRef: PropTypes.any,
  dndReportPositionRef: PropTypes.any,
  onReportIdChange: PropTypes.func
}

Report.displayName = 'Report'

export default Report
